import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import styled from "styled-components";

import { FieldOption, resolveOptionLabel } from "./optionLabel";

/**
 * テキスト編集 + 候補からの選択を両立できる入力フィールド。
 *
 * HTML5 の `<datalist>` ではブラウザネイティブの黒背景/白文字スタイルが
 * 制御できないため、自前のドロップダウンポップオーバーで候補を表示する。
 *
 * - 通常の Input と視覚を統一
 * - ユーザーは自由にテキスト入力可能
 * - 右側のドロップダウンアイコンクリックで候補ポップオーバーを開閉
 * - 候補クリックで input に値が入る
 * - input への入力で候補を部分一致絞り込み（空文字なら全表示）
 * - input 外クリックや Escape でポップオーバーを閉じる
 */
interface ComboInputProps {
  label?: string;
  option?: FieldOption | string;
  placeholder?: string;
  width?: string;
  name?: string;
  value?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  /** 候補テキスト一覧 */
  options: string[];
}

const StyledIcon = styled.div.attrs({
  className: "material-symbols-rounded",
})`
  position: absolute;
  top: 50%;
  right: 8px;
  transform: translateY(-50%);
  z-index: 1;
  cursor: pointer;
  color: var(--soremo-light-gray);

  &:hover {
    color: var(--soremo-blue);
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const StyledInput = styled.input`
  width: 100%;
  padding-right: 32px;
`;

const Popover = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 10;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #fff;
  border: 1px solid var(--soremo-border);
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  max-height: 240px;
  overflow-y: auto;
`;

const PopoverItem = styled.li.attrs({ className: "bodytext-13" })<{
  $active: boolean;
}>`
  padding: 8px 12px;
  cursor: pointer;
  background-color: ${({ $active }) =>
    $active ? "var(--soremo-background)" : "transparent"};

  &:hover {
    background-color: var(--soremo-background);
  }
`;

const EmptyItem = styled.li.attrs({ className: "bodytext-13" })`
  padding: 8px 12px;
  color: var(--soremo-light-gray);
  text-align: center;
`;

const ComboInput: React.FC<ComboInputProps> = ({
  label = "",
  option = "",
  placeholder = "",
  width = "100%",
  name,
  value,
  onChange,
  options,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // 表示モード:
  //  - "all"    : アイコンクリックで開いた状態。常に全候補を表示。
  //  - "filter" : ユーザーが入力中。input value で部分一致フィルタ。
  const [mode, setMode] = useState<"all" | "filter">("filter");
  const filterText = (value ?? "").toLowerCase();
  const filteredOptions =
    mode === "all"
      ? options
      : filterText === ""
        ? options
        : options.filter((o) => o.toLowerCase().includes(filterText));

  // 外クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setMode("filter");
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const handleIconClick = () => {
    // アイコンクリックは常に全候補を表示
    setMode("all");
    setOpen((prev) => !prev);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleSelect = useCallback(
    (selected: string) => {
      // 合成イベントを直接 onChange に渡せないため、ターゲット情報を最小限で組み立てる
      const synthetic = {
        target: { value: selected, name: name ?? "" },
      } as unknown as ChangeEvent<HTMLInputElement>;
      onChange?.(synthetic);
      setOpen(false);
      setMode("filter");
    },
    [onChange, name],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) =>
        filteredOptions.length === 0
          ? -1
          : (i + 1 + filteredOptions.length) % filteredOptions.length,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIndex((i) =>
        filteredOptions.length === 0
          ? -1
          : (i - 1 + filteredOptions.length) % filteredOptions.length,
      );
    } else if (e.key === "Enter") {
      if (open && activeIndex >= 0 && activeIndex < filteredOptions.length) {
        e.preventDefault();
        handleSelect(filteredOptions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // タイピング時は filter モードに切り替えて部分一致絞り込み
    setMode("filter");
    onChange?.(e);
    setOpen(true);
    setActiveIndex(-1);
  };

  const { text: optionLabel, className: optionClass } = resolveOptionLabel(option);
  const containerClass =
    width === "100%"
      ? "u-col u-w100 u-gap4 u-relative"
      : "u-col u-fill u-gap4 u-relative";

  return (
    <div className={containerClass}>
      {label && (
        <label className="bodytext-13 u-line-height-100 u-pl4">
          {label}
          {optionLabel && <span className={optionClass}>{optionLabel}</span>}
        </label>
      )}
      <InputWrapper ref={wrapperRef}>
        <StyledInput
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          name={name}
          value={value ?? ""}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          autoComplete="off"
        />
        <StyledIcon onClick={handleIconClick}>arrow_drop_down</StyledIcon>
        {open && (
          <Popover role="listbox">
            {filteredOptions.length === 0 ? (
              <EmptyItem>候補なし</EmptyItem>
            ) : (
              filteredOptions.map((opt, idx) => (
                <PopoverItem
                  key={opt}
                  role="option"
                  aria-selected={idx === activeIndex}
                  $active={idx === activeIndex}
                  onMouseDown={(e) => {
                    // input の blur より先に確定させる
                    e.preventDefault();
                    handleSelect(opt);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                >
                  {opt}
                </PopoverItem>
              ))
            )}
          </Popover>
        )}
      </InputWrapper>
    </div>
  );
};

export default ComboInput;
